<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import type { BankWiringData } from '@Types/BankWiringData.interface';
import { useValidateBank } from '@Shared';

const props = withDefaults(defineProps<{
    bankWiringData: BankWiringData | null;
    isLoading: boolean;
    showLogo?: boolean;
}>(), {
    showLogo: false,
});

const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL as string;
const { isWireStatusTransactable } = useValidateBank();

const logoVisible = ref(false);

const beneficiaryBank = computed(() => props.bankWiringData?.beneficiaryBank ?? null);

const canTransactWires = computed(() => isWireStatusTransactable(beneficiaryBank.value?.wireStatus ?? null));

const bankVisible = computed(() => !props.isLoading && !!beneficiaryBank.value?.name);

const logoUrl = computed(() =>
    beneficiaryBank.value?.routingNumber
        ? `${cdnBaseUrl}/bank/${beneficiaryBank.value.routingNumber}.png`
        : null
);

// Reset logo visibility whenever a new bank is resolved
watch(
    () => beneficiaryBank.value?.routingNumber,
    (routingNumber) => { logoVisible.value = !!routingNumber; },
    { immediate: true }
);

const onLogoError = () => {
    logoVisible.value = false;
};
</script>

<template>
    <app-skeleton v-if="isLoading" type="text" :count="2" widths="30%, 90%" />

    <app-form-content-layout
        v-else-if="bankVisible"
        class="bank-lookup"
        :class="{ 'bank-lookup--error': !canTransactWires }"
    >
        <div class="bank-lookup__status" borderOffset="0rem">
            <app-icon :icon="canTransactWires ? 'cd-ic-check-square' : 'cd-ic-alert-square'" />
            <span>{{ canTransactWires ? 'Routing number matched:' : 'This bank is unable to receive wires.' }}</span>
        </div>

        <p>
            <strong>{{ beneficiaryBank!.name }}</strong>
        </p>
        <div 
            v-if="showLogo && logoVisible && logoUrl"
            class="bank-lookup__logo-container"
        >
            <img
                :src="logoUrl"
                class="bank-lookup__logo"
                alt="Bank logo"
                @error="onLogoError"
            />
        </div>
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

$radius: 0.25rem;

.bank-lookup {
    padding: 1rem;
    border: 1px solid $dark-green;
    border-radius: $radius;
    background-color: $white;

    &--error {
        border-color: $red;

        .bank-lookup__status {
            color: $red;
            font-weight: normal;
        }
    }

    &__status {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.9rem;
        font-weight: bold;
        color: $dark-green;
    }

    &__logo-container {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__logo {
        max-width: 50%;
        max-height: 48px;
        object-fit: contain;
    }
}
</style>
